import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
    Box,
    Typography,
    Paper,
    CircularProgress,
    Alert,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    SelectChangeEvent,
    Button,
    Chip,
    Card,
    CardContent,
    Slider,
    Tooltip,
    IconButton,
    Stack,
    Grid,
    Divider
} from '@mui/material';
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { getEnergyPotentialHeatmap, getNearestEnergyPotential, getLocationFromCoordinates } from '../../services/api';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import InfoIcon from '@mui/icons-material/Info';
import SolarPowerIcon from '@mui/icons-material/WbSunny';
import WindPowerIcon from '@mui/icons-material/Air';
import WaterIcon from '@mui/icons-material/Water';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import GrassIcon from '@mui/icons-material/Grass';
import TableChartIcon from '@mui/icons-material/TableChart';
import AddIcon from '@mui/icons-material/Add';

// Fix for Leaflet marker icons in React
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

let DefaultIcon = L.icon({
    iconUrl: icon,
    shadowUrl: iconShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41]
});

let UserLocationIcon = L.icon({
    iconUrl: icon,
    shadowUrl: iconShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    className: 'pulse-marker'
});

L.Marker.prototype.options.icon = DefaultIcon;

// Color scales for different energy types
const colorScales = {
    solar: [
        { threshold: 20, color: '#FFECA0' }, // Light yellow
        { threshold: 40, color: '#FFDC73' }, // Pale yellow
        { threshold: 60, color: '#FFD147' }, // Medium yellow
        { threshold: 80, color: '#FFC20A' }, // Strong yellow
        { threshold: 100, color: '#FFB100' }  // Deep yellow/orange
    ],
    wind: [
        { threshold: 20, color: '#B3E5FC' }, // Light blue
        { threshold: 40, color: '#81D4FA' }, // Pale blue
        { threshold: 60, color: '#4FC3F7' }, // Medium blue
        { threshold: 80, color: '#29B6F6' }, // Strong blue
        { threshold: 100, color: '#03A9F4' }  // Deep blue
    ],
    hydro: [
        { threshold: 20, color: '#B2DFDB' }, // Light teal
        { threshold: 40, color: '#80CBC4' }, // Pale teal
        { threshold: 60, color: '#4DB6AC' }, // Medium teal
        { threshold: 80, color: '#26A69A' }, // Strong teal
        { threshold: 100, color: '#009688' }  // Deep teal
    ],
    geothermal: [
        { threshold: 20, color: '#FFCCBC' }, // Light orange
        { threshold: 40, color: '#FFAB91' }, // Pale orange
        { threshold: 60, color: '#FF8A65' }, // Medium orange
        { threshold: 80, color: '#FF7043' }, // Strong orange
        { threshold: 100, color: '#FF5722' }  // Deep orange
    ],
    biomass: [
        { threshold: 20, color: '#C8E6C9' }, // Light green
        { threshold: 40, color: '#A5D6A7' }, // Pale green
        { threshold: 60, color: '#81C784' }, // Medium green
        { threshold: 80, color: '#66BB6A' }, // Strong green
        { threshold: 100, color: '#4CAF50' }  // Deep green
    ]
};

// Get color based on the potential score
const getColor = (score: number, energyType: string): string => {
    const scale = colorScales[energyType as keyof typeof colorScales] || colorScales.solar;

    for (const { threshold, color } of scale) {
        if (score <= threshold) {
            return color;
        }
    }
    return scale[scale.length - 1].color; // Default to highest color
};

// Component to set the map view
interface SetViewProps {
    center: [number, number];
    zoom: number;
}

const SetView: React.FC<SetViewProps> = ({ center, zoom }) => {
    const map = useMap();
    map.setView(center, zoom);
    return null;
};

// Define proper interfaces for energy types
interface EnergySubType {
    potential: number;
}

interface SolarEnergy extends EnergySubType {
    annualOutput: number;
    installationFeasibility: number;
}

interface WindEnergy extends EnergySubType {
    averageSpeed: number | string;
    annualOutput: number;
}

interface HydroEnergy extends EnergySubType {
    // No additional props required for hydro
}

interface GeothermalEnergy extends EnergySubType {
    temperature: number;
    depth: number;
}

interface BiomassEnergy extends EnergySubType {
    availableFeedstock: number;
}

// Define the complete EnergyPotentialPoint interface
interface EnergyPotentialPoint {
    latitude: number;
    longitude: number;
    region: string;
    state: string;
    country: string;
    solar: SolarEnergy;
    wind: WindEnergy;
    hydro: HydroEnergy;
    geothermal: GeothermalEnergy;
    biomass: BiomassEnergy;
    recommendations: {
        primarySource: string;
        secondarySource: string;
        reasonPrimary: string;
        reasonSecondary: string;
    };
}

// Generate mock energy data for fallback
const generateMockEnergyData = (centerLat: number, centerLng: number, count: number = 10): EnergyPotentialPoint[] => {
    const mockData: EnergyPotentialPoint[] = [];

    for (let i = 0; i < count; i++) {
        // Generate random offsets around the center point
        const latOffset = (Math.random() - 0.5) * 5;  // +/- 2.5 degrees
        const lngOffset = (Math.random() - 0.5) * 5;  // +/- 2.5 degrees

        // Generate random potential values between 20 and 95
        const solarPotential = Math.floor(Math.random() * 75) + 20;
        const windPotential = Math.floor(Math.random() * 75) + 20;
        const hydroPotential = Math.floor(Math.random() * 75) + 20;
        const geothermalPotential = Math.floor(Math.random() * 75) + 20;
        const biomassPotential = Math.floor(Math.random() * 75) + 20;

        // Determine primary and secondary recommendations based on highest potentials
        const potentials = [
            { type: 'solar', value: solarPotential },
            { type: 'wind', value: windPotential },
            { type: 'hydro', value: hydroPotential },
            { type: 'geothermal', value: geothermalPotential },
            { type: 'biomass', value: biomassPotential }
        ];

        potentials.sort((a, b) => b.value - a.value);

        mockData.push({
            latitude: centerLat + latOffset,
            longitude: centerLng + lngOffset,
            region: `Region ${i + 1}`,
            state: 'State',
            country: 'Country',
            solar: {
                potential: solarPotential,
                annualOutput: Math.floor(Math.random() * 2000) + 1000,
                installationFeasibility: Math.floor(Math.random() * 30) + 70
            },
            wind: {
                potential: windPotential,
                averageSpeed: (Math.random() * 10 + 2).toFixed(1),
                annualOutput: Math.floor(Math.random() * 5000) + 3000
            },
            hydro: {
                potential: hydroPotential
            },
            geothermal: {
                potential: geothermalPotential,
                temperature: Math.floor(Math.random() * 50) + 70,
                depth: Math.floor(Math.random() * 200) + 100
            },
            biomass: {
                potential: biomassPotential,
                availableFeedstock: Math.floor(Math.random() * 5000) + 1000
            },
            recommendations: {
                primarySource: potentials[0].type as string,
                secondarySource: potentials[1].type as string,
                reasonPrimary: `High ${potentials[0].type} potential in this area with favorable conditions.`,
                reasonSecondary: `Good ${potentials[1].type} potential as a supplementary energy source.`
            }
        });
    }

    return mockData;
};

// New interface component
const NavigationTabs: React.FC<{
    activeTab: string;
    setActiveTab: (tab: string) => void;
    onLocateMe: () => void;
    locating: boolean;
}> = ({ activeTab, setActiveTab, onLocateMe, locating }) => {
    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: 'center',
                justifyContent: { xs: 'center', sm: 'flex-start' },
                gap: { xs: 1, sm: 0 },
                width: '100%',
                borderRadius: '12px',
                bgcolor: 'background.paper',
                boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
                mb: 3,
                overflow: 'hidden'
            }}
        >
            <Box
                sx={{
                    py: 1.5,
                    px: 3,
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    color: activeTab === 'locate' ? 'primary.main' : 'text.secondary',
                    bgcolor: activeTab === 'locate' ? 'primary.light' : 'transparent',
                    opacity: activeTab === 'locate' ? 1 : 0.8,
                    transition: 'all 0.2s ease',
                    borderBottom: { xs: 0, sm: activeTab === 'locate' ? '3px solid' : '3px solid transparent' },
                    borderColor: 'primary.main',
                    flexGrow: { xs: 1, sm: 0 },
                    width: { xs: '100%', sm: 'auto' },
                    justifyContent: 'center',
                    '&:hover': {
                        bgcolor: activeTab === 'locate' ? 'primary.light' : 'rgba(0, 0, 0, 0.04)',
                        color: activeTab === 'locate' ? 'primary.main' : 'primary.dark',
                    }
                }}
                onClick={onLocateMe}
            >
                <MyLocationIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
                <Typography variant="button" fontWeight={activeTab === 'locate' ? 600 : 400}>
                    LOCATE ME
                </Typography>
                {locating && <CircularProgress size={16} sx={{ ml: 1.5, color: 'primary.main' }} />}
            </Box>

            <Box
                sx={{
                    py: 1.5,
                    px: 3,
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    color: activeTab === 'heatmap' ? 'primary.main' : 'text.secondary',
                    bgcolor: activeTab === 'heatmap' ? 'primary.light' : 'transparent',
                    opacity: activeTab === 'heatmap' ? 1 : 0.8,
                    transition: 'all 0.2s ease',
                    borderBottom: { xs: 0, sm: activeTab === 'heatmap' ? '3px solid' : '3px solid transparent' },
                    borderColor: 'primary.main',
                    flexGrow: { xs: 1, sm: 0 },
                    width: { xs: '100%', sm: 'auto' },
                    justifyContent: 'center',
                    '&:hover': {
                        bgcolor: activeTab === 'heatmap' ? 'primary.light' : 'rgba(0, 0, 0, 0.04)',
                        color: activeTab === 'heatmap' ? 'primary.main' : 'primary.dark',
                    }
                }}
                onClick={() => setActiveTab('heatmap')}
            >
                <SolarPowerIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
                <Typography variant="button" fontWeight={activeTab === 'heatmap' ? 600 : 400}>
                    HEAT MAP
                </Typography>
            </Box>

            <Box
                sx={{
                    py: 1.5,
                    px: 3,
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    color: activeTab === 'data' ? 'primary.main' : 'text.secondary',
                    bgcolor: activeTab === 'data' ? 'primary.light' : 'transparent',
                    opacity: activeTab === 'data' ? 1 : 0.8,
                    transition: 'all 0.2s ease',
                    borderBottom: { xs: 0, sm: activeTab === 'data' ? '3px solid' : '3px solid transparent' },
                    borderColor: 'primary.main',
                    flexGrow: { xs: 1, sm: 0 },
                    width: { xs: '100%', sm: 'auto' },
                    justifyContent: 'center',
                    '&:hover': {
                        bgcolor: activeTab === 'data' ? 'primary.light' : 'rgba(0, 0, 0, 0.04)',
                        color: activeTab === 'data' ? 'primary.main' : 'primary.dark',
                    }
                }}
                onClick={() => setActiveTab('data')}
            >
                <TableChartIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
                <Typography variant="button" fontWeight={activeTab === 'data' ? 600 : 400}>
                    DATA TABLE
                </Typography>
            </Box>

            <Box
                sx={{
                    py: 1.5,
                    px: 3,
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    color: activeTab === 'add' ? 'primary.main' : 'text.secondary',
                    bgcolor: activeTab === 'add' ? 'primary.light' : 'transparent',
                    opacity: activeTab === 'add' ? 1 : 0.8,
                    transition: 'all 0.2s ease',
                    borderBottom: { xs: 0, sm: activeTab === 'add' ? '3px solid' : '3px solid transparent' },
                    borderColor: 'primary.main',
                    flexGrow: { xs: 1, sm: 0 },
                    width: { xs: '100%', sm: 'auto' },
                    justifyContent: 'center',
                    '&:hover': {
                        bgcolor: activeTab === 'add' ? 'primary.light' : 'rgba(0, 0, 0, 0.04)',
                        color: activeTab === 'add' ? 'primary.main' : 'primary.dark',
                    }
                }}
                onClick={() => setActiveTab('add')}
            >
                <AddIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
                <Typography variant="button" fontWeight={activeTab === 'add' ? 600 : 400}>
                    ADD DATA
                </Typography>
            </Box>
        </Box>
    );
};

// Create a custom component to access the map instance
const MapController: React.FC<{ setMapRef: (map: L.Map) => void }> = ({ setMapRef }) => {
    const map = useMap();

    // Effect to set the map ref once the map is available
    useEffect(() => {
        setMapRef(map);
    }, [map, setMapRef]);

    return null;
};

// Main component
const EnergyHeatmap: React.FC = () => {
    const [heatmapData, setHeatmapData] = useState<EnergyPotentialPoint[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [userLocation, setUserLocation] = useState<[number, number] | null>(null);
    const [selectedLocation, setSelectedLocation] = useState<EnergyPotentialPoint | null>(null);
    const [energyType, setEnergyType] = useState<string>('solar');
    const [radiusFilter, setRadiusFilter] = useState<number>(5000); // 5000km default
    const [filteredData, setFilteredData] = useState<EnergyPotentialPoint[]>([]);
    const [locationLoading, setLocationLoading] = useState<boolean>(false);
    const mapRef = useRef<L.Map | null>(null);
    const [activeTab, setActiveTab] = useState<string>('heatmap');

    // Load heatmap data
    useEffect(() => {
        const fetchHeatmapData = async () => {
            try {
                setLoading(true);
                let data;
                try {
                    data = await getEnergyPotentialHeatmap();
                } catch (error) {
                    console.warn('Error fetching energy heatmap data from API, using fallback data:', error);
                    // Use fallback mock data if API fails
                    const defaultLat = 13.0827;  // Default to a common location
                    const defaultLng = 80.2707;
                    data = generateMockEnergyData(defaultLat, defaultLng, 15);
                }

                if (!data || data.length === 0) {
                    console.warn('No data returned from API, using fallback data');
                    const defaultLat = 13.0827;
                    const defaultLng = 80.2707;
                    data = generateMockEnergyData(defaultLat, defaultLng, 15);
                }

                setHeatmapData(data);
                setFilteredData(data);
                setLoading(false);
            } catch (error) {
                console.error('Error in energy heatmap data flow:', error);
                setError('Failed to load energy potential heatmap data');
                setLoading(false);
            }
        };

        fetchHeatmapData();
    }, []);

    // Filter data based on radius around user location
    useEffect(() => {
        if (!userLocation || !heatmapData.length) return;

        const [userLat, userLng] = userLocation;

        // Filter data points within the radius
        const filtered = heatmapData.filter(point => {
            // Calculate distance in kilometers using Haversine formula
            const lat1 = userLat * Math.PI / 180;
            const lat2 = point.latitude * Math.PI / 180;
            const deltaLat = (point.latitude - userLat) * Math.PI / 180;
            const deltaLng = (point.longitude - userLng) * Math.PI / 180;

            const a = Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
                Math.cos(lat1) * Math.cos(lat2) *
                Math.sin(deltaLng / 2) * Math.sin(deltaLng / 2);
            const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
            const distance = 6371 * c; // Earth's radius in km * c

            return distance <= radiusFilter;
        });

        setFilteredData(filtered);
    }, [userLocation, heatmapData, radiusFilter]);

    // Get user location
    const getUserLocation = useCallback(() => {
        setLocationLoading(true);
        setError(null);

        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                async (position) => {
                    const { latitude, longitude } = position.coords;
                    setUserLocation([latitude, longitude]);

                    try {
                        // Get location information using multiple geocoding services with fallbacks
                        const locationInfo = await getLocationFromCoordinates(latitude, longitude);
                        console.log('Location data retrieved:', locationInfo);

                        // Get energy potential for this location with real location data
                        let nearestPoint: EnergyPotentialPoint;
                        try {
                            nearestPoint = await getEnergyPotentialWithRetry(latitude, longitude);
                        } catch (error) {
                            console.error('Error getting nearest energy potential, using mock data:', error);
                            // Generate a single mock data point for the user's location
                            const mockDataPoints = generateMockEnergyData(latitude, longitude, 1);
                            nearestPoint = mockDataPoints[0];
                        }

                        // Update the location information with real data from geocoding
                        if (nearestPoint && locationInfo) {
                            if (locationInfo.country) {
                                nearestPoint.country = locationInfo.country;
                            }
                            if (locationInfo.state) {
                                nearestPoint.state = locationInfo.state;
                            }
                            if (locationInfo.city) {
                                nearestPoint.region = locationInfo.city;
                            }
                        }

                        setSelectedLocation(nearestPoint);
                    } catch (error) {
                        console.error('Error getting location information:', error);
                        setError('Could not determine your location. Please check your internet connection and try again.');
                    }

                    setLocationLoading(false);
                },
                (error) => {
                    console.error('Error getting user location:', error);
                    setError('Could not access your location. Please check your browser permissions and try again.');
                    setLocationLoading(false);

                    // Set a default location and generate mock data around it
                    const defaultLat = 13.0827;  // Default location
                    const defaultLng = 80.2707;
                    setUserLocation([defaultLat, defaultLng]);

                    const mockDataPoints = generateMockEnergyData(defaultLat, defaultLng, 15);
                    setHeatmapData(mockDataPoints);
                    setFilteredData(mockDataPoints);

                    // Set a mock selected location
                    setSelectedLocation(mockDataPoints[0]);
                },
                {
                    enableHighAccuracy: true,
                    timeout: 10000,
                    maximumAge: 0
                }
            );
        } else {
            setError('Geolocation is not supported by your browser. Please use a modern browser that supports geolocation.');
            setLocationLoading(false);

            // Set a default location and generate mock data
            const defaultLat = 13.0827;
            const defaultLng = 80.2707;
            setUserLocation([defaultLat, defaultLng]);

            const mockDataPoints = generateMockEnergyData(defaultLat, defaultLng, 15);
            setHeatmapData(mockDataPoints);
            setFilteredData(mockDataPoints);

            // Set a mock selected location
            setSelectedLocation(mockDataPoints[0]);
        }
    }, []);

    // Helper function to retry getting energy potential data
    const getEnergyPotentialWithRetry = async (latitude: number, longitude: number, maxRetries = 3): Promise<EnergyPotentialPoint> => {
        let lastError;

        for (let attempt = 0; attempt < maxRetries; attempt++) {
            try {
                console.log(`Attempting to get energy potential data (attempt ${attempt + 1}/${maxRetries})`);
                const data = await getNearestEnergyPotential(latitude, longitude);
                return data;
            } catch (error) {
                console.error(`Attempt ${attempt + 1} failed:`, error);
                lastError = error;
                // Wait between retries (increasing delay with each attempt)
                if (attempt < maxRetries - 1) {
                    await new Promise(resolve => setTimeout(resolve, 1000 * (attempt + 1)));
                }
            }
        }

        // If all retries fail, throw the last error
        throw lastError || new Error('Failed to get energy potential data after multiple attempts');
    };

    // Initialize user location on component mount
    useEffect(() => {
        getUserLocation();
    }, [getUserLocation]);

    // Handle energy type change
    const handleEnergyTypeChange = (event: SelectChangeEvent) => {
        setEnergyType(event.target.value);
    };

    // Handle radius filter change
    const handleRadiusChange = (event: Event, value: number | number[]) => {
        setRadiusFilter(value as number);
    };

    // Get the appropriate icon for each energy type
    const getEnergyIcon = (type: string) => {
        switch (type) {
            case 'solar': return <SolarPowerIcon />;
            case 'wind': return <WindPowerIcon />;
            case 'hydro': return <WaterIcon />;
            case 'geothermal': return <LocalFireDepartmentIcon />;
            case 'biomass': return <GrassIcon />;
            default: return <InfoIcon />;
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 2 }}>
                <Alert
                    severity="error"
                    action={
                        <Button color="inherit" size="small" onClick={getUserLocation}>
                            Retry
                        </Button>
                    }
                >
                    {error}
                </Alert>
                <Box sx={{ mt: 2, p: 2, border: '1px dashed #ccc', borderRadius: 1 }}>
                    <Typography variant="subtitle1" gutterBottom>
                        Troubleshooting Tips:
                    </Typography>
                    <Typography variant="body2" component="ul" sx={{ pl: 2 }}>
                        <li>Check that your browser has permission to access your location</li>
                        <li>Ensure you have a stable internet connection</li>
                        <li>Try refreshing the page and allowing location access when prompted</li>
                        <li>If using a VPN, try disabling it temporarily</li>
                    </Typography>
                    <Box sx={{ mt: 2 }}>
                        <Button
                            variant="contained"
                            onClick={() => {
                                window.location.reload();
                            }}
                        >
                            Refresh Page
                        </Button>
                    </Box>
                </Box>
            </Box>
        );
    }

    return (
        <>
            {error && <Alert severity="error">{error}</Alert>}

            <Paper
                elevation={0}
                sx={{
                    p: { xs: 2, sm: 3, md: 4 },
                    height: '100%',
                    borderRadius: '16px',
                    border: '1px solid rgba(0,0,0,0.05)',
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
                }}
            >
                <Box sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    justifyContent: 'space-between',
                    mb: 2,
                }}>
                    <Box>
                        <Typography
                            variant="h4"
                            sx={{
                                mb: 0.5,
                                fontWeight: 700,
                                color: 'primary.dark',
                                fontSize: { xs: '1.75rem', sm: '2rem', md: '2.25rem' }
                            }}
                        >
                            Global Renewable Energy Potential
                        </Typography>
                        <Typography
                            variant="subtitle1"
                            color="text.secondary"
                            sx={{ mb: 1 }}
                        >
                            Explore energy potential and viability across different regions
                        </Typography>
                    </Box>

                    <FormControl sx={{ minWidth: 200, mt: { xs: 2, sm: 0 } }}>
                        <InputLabel id="energy-type-label">Energy Type</InputLabel>
                        <Select
                            labelId="energy-type-label"
                            id="energy-type-select"
                            value={energyType}
                            label="Energy Type"
                            onChange={handleEnergyTypeChange}
                            size="small"
                            sx={{
                                '& .MuiSelect-select': {
                                    display: 'flex',
                                    alignItems: 'center'
                                }
                            }}
                        >
                            <MenuItem value="solar">
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <SolarPowerIcon sx={{ mr: 1, color: '#FFC107' }} />
                                    <Typography>Solar Energy</Typography>
                                </Box>
                            </MenuItem>
                            <MenuItem value="wind">
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <WindPowerIcon sx={{ mr: 1, color: '#2196F3' }} />
                                    <Typography>Wind Energy</Typography>
                                </Box>
                            </MenuItem>
                            <MenuItem value="hydro">
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <WaterIcon sx={{ mr: 1, color: '#00BCD4' }} />
                                    <Typography>Hydro Energy</Typography>
                                </Box>
                            </MenuItem>
                            <MenuItem value="geothermal">
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <LocalFireDepartmentIcon sx={{ mr: 1, color: '#FF5722' }} />
                                    <Typography>Geothermal Energy</Typography>
                                </Box>
                            </MenuItem>
                            <MenuItem value="biomass">
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <GrassIcon sx={{ mr: 1, color: '#4CAF50' }} />
                                    <Typography>Biomass Energy</Typography>
                                </Box>
                            </MenuItem>
                        </Select>
                    </FormControl>
                </Box>

                {/* Add Navigation Tabs */}
                <NavigationTabs
                    activeTab={activeTab}
                    setActiveTab={setActiveTab}
                    onLocateMe={getUserLocation}
                    locating={locationLoading}
                />

                <Box sx={{ flexGrow: 1, mt: 3 }}>
                    <Box display="grid" gridTemplateColumns="repeat(12, 1fr)" gap={2}>
                        <Box gridColumn="span 9">
                            {loading ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                                    <CircularProgress />
                                </Box>
                            ) : error ? (
                                <Alert severity="error">{error}</Alert>
                            ) : (
                                <Box sx={{ height: 500, width: '100%', position: 'relative', border: '1px solid #e0e0e0', borderRadius: '8px' }} className="map-container">
                                    <MapContainer
                                        center={userLocation || [20, 0]}
                                        zoom={7}
                                        style={{ height: '100%', width: '100%', borderRadius: '8px' }}
                                    >
                                        <MapController setMapRef={(map) => { mapRef.current = map; }} />
                                        <TileLayer
                                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                        />
                                        {userLocation && (
                                            <>
                                                <SetView center={userLocation} zoom={7} />
                                                <Marker position={userLocation} icon={UserLocationIcon}>
                                                    <Popup>
                                                        <Typography variant="body2">
                                                            <strong>Your Location</strong>
                                                            {selectedLocation && (
                                                                <div style={{ marginTop: '8px' }}>
                                                                    {selectedLocation.region}, {selectedLocation.state}, {selectedLocation.country}
                                                                </div>
                                                            )}
                                                            <div style={{ marginTop: '4px', fontSize: '0.8em', color: '#666' }}>
                                                                Coordinates: {userLocation[0].toFixed(5)}, {userLocation[1].toFixed(5)}
                                                            </div>
                                                        </Typography>
                                                    </Popup>
                                                </Marker>
                                            </>
                                        )}

                                        {/* Add a helpful message when no data points are visible */}
                                        {filteredData.length === 0 && userLocation && (
                                            <div style={{
                                                position: 'absolute',
                                                top: '50%',
                                                left: '50%',
                                                transform: 'translate(-50%, -50%)',
                                                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                                padding: '15px',
                                                borderRadius: '5px',
                                                zIndex: 1000
                                            }}>
                                                <Typography variant="body1">
                                                    No energy potential data points in this area. Try:
                                                </Typography>
                                                <ul style={{ margin: '10px 0', paddingLeft: '20px' }}>
                                                    <li>Increasing the radius filter</li>
                                                    <li>Moving to a different location</li>
                                                    <li>Selecting a different energy type</li>
                                                </ul>
                                                <Button
                                                    variant="contained"
                                                    size="small"
                                                    onClick={() => setRadiusFilter(Math.min(radiusFilter + 100, 500))}
                                                >
                                                    Increase Radius
                                                </Button>
                                            </div>
                                        )}

                                        {filteredData.map((point, index) => {
                                            // Get potential value for the selected energy type
                                            let potentialValue = 0;

                                            if (energyType === 'solar' && point.solar) {
                                                potentialValue = point.solar.potential;
                                            } else if (energyType === 'wind' && point.wind) {
                                                potentialValue = point.wind.potential;
                                            } else if (energyType === 'hydro' && point.hydro) {
                                                potentialValue = point.hydro.potential;
                                            } else if (energyType === 'geothermal' && point.geothermal) {
                                                potentialValue = point.geothermal.potential;
                                            } else if (energyType === 'biomass' && point.biomass) {
                                                potentialValue = point.biomass.potential;
                                            }

                                            const color = getColor(potentialValue, energyType);

                                            return (
                                                <Circle
                                                    key={index}
                                                    center={[point.latitude, point.longitude]}
                                                    radius={30000} // 30km radius
                                                    pathOptions={{
                                                        color: color,
                                                        fillColor: color,
                                                        fillOpacity: 0.7
                                                    }}
                                                    eventHandlers={{
                                                        click: () => setSelectedLocation(point)
                                                    }}
                                                >
                                                    <Popup>
                                                        <Typography variant="subtitle2">{point.region}, {point.country}</Typography>
                                                        <Typography variant="body2">
                                                            {energyType.charAt(0).toUpperCase() + energyType.slice(1)} Potential: {potentialValue}%
                                                        </Typography>
                                                    </Popup>
                                                </Circle>
                                            );
                                        })}
                                        {userLocation && (
                                            <Circle
                                                center={userLocation}
                                                radius={radiusFilter * 1000}
                                                pathOptions={{ color: '#2196F3', fillOpacity: 0.1, weight: 2, dashArray: '5, 5' }}
                                            />
                                        )}
                                    </MapContainer>
                                </Box>
                            )}
                        </Box>
                        <Box gridColumn="span 3">
                            <Box sx={{ height: '100%' }}>
                                <Card sx={{ height: '100%' }}>
                                    <CardContent>
                                        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                                            <InputLabel id="energy-type-label">Energy Type</InputLabel>
                                            <Select
                                                labelId="energy-type-label"
                                                value={energyType}
                                                label="Energy Type"
                                                onChange={handleEnergyTypeChange}
                                            >
                                                <MenuItem value="solar"><Box sx={{ display: 'flex', alignItems: 'center' }}><SolarPowerIcon fontSize="small" sx={{ mr: 1 }} /> Solar</Box></MenuItem>
                                                <MenuItem value="wind"><Box sx={{ display: 'flex', alignItems: 'center' }}><WindPowerIcon fontSize="small" sx={{ mr: 1 }} /> Wind</Box></MenuItem>
                                                <MenuItem value="hydro"><Box sx={{ display: 'flex', alignItems: 'center' }}><WaterIcon fontSize="small" sx={{ mr: 1 }} /> Hydro</Box></MenuItem>
                                                <MenuItem value="geothermal"><Box sx={{ display: 'flex', alignItems: 'center' }}><LocalFireDepartmentIcon fontSize="small" sx={{ mr: 1 }} /> Geothermal</Box></MenuItem>
                                                <MenuItem value="biomass"><Box sx={{ display: 'flex', alignItems: 'center' }}><GrassIcon fontSize="small" sx={{ mr: 1 }} /> Biomass</Box></MenuItem>
                                            </Select>
                                        </FormControl>

                                        <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                                            {getEnergyIcon(energyType)}
                                            <Box component="span" sx={{ ml: 1 }}>{energyType.charAt(0).toUpperCase() + energyType.slice(1)} Energy Potential</Box>
                                        </Typography>

                                        <Stack
                                            direction="row"
                                            spacing={1}
                                            sx={{
                                                flexWrap: 'wrap',
                                                gap: 1,
                                                '& .MuiChip-root': {
                                                    marginBottom: 1
                                                }
                                            }}
                                        >
                                            {colorScales[energyType as keyof typeof colorScales]?.map((item, index) => (
                                                <Tooltip key={index} title={`Up to ${item.threshold}% potential`}>
                                                    <Chip
                                                        label={`${index === 0 ? '0' : colorScales[energyType as keyof typeof colorScales][index - 1].threshold}% - ${item.threshold}%`}
                                                        sx={{
                                                            bgcolor: item.color,
                                                            color: ['solar', 'biomass'].includes(energyType) ? 'text.primary' : 'white',
                                                            fontWeight: 'medium',
                                                            minWidth: '80px',
                                                            justifyContent: 'center'
                                                        }}
                                                        className={`energy-legend-chip energy-type-${energyType}`}
                                                        size="small"
                                                    />
                                                </Tooltip>
                                            ))}
                                        </Stack>

                                        <Divider sx={{ my: 2 }} />

                                        <Typography gutterBottom>
                                            Filter radius: {radiusFilter} km
                                        </Typography>
                                        <Slider
                                            value={radiusFilter}
                                            onChange={handleRadiusChange}
                                            min={100}
                                            max={5000}
                                            step={100}
                                            valueLabelDisplay="auto"
                                            valueLabelFormat={(value) => `${value} km`}
                                            sx={{ mb: 1 }}
                                        />
                                    </CardContent>
                                </Card>
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Paper>
        </>
    );
};

export default EnergyHeatmap; 